import Chart from 'react-apexcharts'
import {useSelector} from 'react-redux'
import {useEffect, useState} from 'react'
import localStorageManager from '../../../utils/localStorageManager'
import localStorageKeys from '../../../utils/localStorageKeys'
import moment from 'moment'
import {colors} from '../../../utils/colors'
import roundValue from '../../../utils/math'
import renderSkeletonLoaders from '../../../utils/loader'

const MINIMUM_REQUIRED_DATA_COUNT = 0
const MINIMUM_MARKERS_COUNT = 1
const MARKERS_VISIBLE_SIZE = 5
const MARKERS_INVISIBLE_SIZE = 0
const CUSTOM_COLORS = {
    EU: colors.colorBlue,
    AZ: colors.colorBlue,
    US: colors.colorLightGreen,
    AU: colors.colorRed,
    AS: colors.colorFilterTitle
}

const CustomLegend = ({series, showRegionsLoader, selectRegions, toggleSeriesVisibility, activeSeries}) => {
    return (
        <div className="custom-legend apexcharts-legend apexcharts-align-center apx-legend-position-bottom">
            {selectRegions.map((region) => (
                <div
                    key={region.value}
                    className={`apexcharts-legend-series ${activeSeries.includes(region.label) ? '' : 'legend-deactive'}`}
                    onClick={() => toggleSeriesVisibility(region.label)}
                >
                    {showRegionsLoader && !series.find(s => s.name === region.label) ? (
                        <span>{renderSkeletonLoaders(1, 'switch-skeleton')}</span>
                    ) : (
                        <>
                            <span className="custom-legend-marker apexcharts-legend-marker" style={{backgroundColor: CUSTOM_COLORS[region.label]}}></span>
                            <span className="apexcharts-legend-text">{region.label}</span>
                        </>
                    )}
                </div>
            ))}
        </div>
    )
}

const LineChart = ({lineChartData, isDarkMode, isAllRegionShowed, showRegionsLoader, selectRegions}) => {
    const resource = useSelector(state => state.monitoring.resource)
    const sidebarIsActive = useSelector(state => state.sidebar.sidebarIsActive)
    const [isLegendShowed, setIsLegendShowed] = useState(false)
    const [activeSeries, setActiveSeries] = useState(selectRegions.map(region => region.label))

    const toggleSeriesVisibility = (seriesName) => {
        setActiveSeries(prevState =>
            prevState.includes(seriesName)
                ? prevState.filter(name => name !== seriesName)
                : [...prevState, seriesName]
        )
    }

    const series = lineChartData?.map(({region, data, categories}) => ({
        name: region,
        data: activeSeries.includes(region)
            ? data?.map((value, index) => ({
                x: isAllRegionShowed ? moment(categories?.[index]).utc().format('YYYY-MM-DDTHH:mm:00[Z]') : categories?.[index] || null,
                y: value || null
            })) || []
            : [],
        color: CUSTOM_COLORS?.[region]
    }))

    const [options, setOptions] = useState({
        colors: series.map(s => s.color || Object.values(CUSTOM_COLORS)?.find(colorCode =>
            !series.some(seriesItem => seriesItem.color === colorCode))
        ),
        series: series,
        chart: {
            events: {
                mounted: (chart) => {
                    chart.windowResizeHandler()
                }
            },
            width: '100%',
            fontFamily: 'SpaceGrotesk, sans-serif',
            zoom: {
                enabled: true
            },
            toolbar: {
                show: true,
                export: {
                    csv: {
                        filename: `${resource?.name}-monitoring`,
                        headerCategory: 'Checked at',
                        dateFormatter(timestamp) {
                            return moment(timestamp).utc().format('HH:mm / DD.MM.YYYY')
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: false,
            enabled: false
        },
        stroke: {
            width: 2
        },
        grid: {
            show: true,
            borderColor: isDarkMode ? colors.colorPrimaryHover : colors.colorLightGrey,
            row: {
                colors: isDarkMode ? [] : [colors.colorWhite, 'transparent'],
                show: !isDarkMode
            },
            column: {
                colors: isDarkMode ? [] : [colors.colorWhite, 'transparent'],
                show: !isDarkMode
            }
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM \'yy',
                    day: 'dd MMM',
                    hour: 'HH:mm'
                }
            },
            axisBorder: {
                show: true,
                color: isDarkMode ? colors.colorPrimaryHover : colors.colorLightGrey
            }
        },
        yaxis: {
            labels: {
                formatter: value => {
                    return value && value % 1 ? roundValue(Number(value)) : value + ' ms'
                }
            }
        },
        tooltip: {
            x: {
                show: true,
                format: 'HH:mm / dd MMM',
                formatter: undefined
            }
        },
        markers: {
            size: lineChartData?.map(({data}) => data?.length === MINIMUM_MARKERS_COUNT ? MARKERS_VISIBLE_SIZE : MARKERS_INVISIBLE_SIZE)
        }
    })

    useEffect(() => {
        setOptions(prevOptions => ({
            ...prevOptions,
            markers: {
                size: lineChartData?.map(({data}) => data?.length === MINIMUM_MARKERS_COUNT ? MARKERS_VISIBLE_SIZE : MARKERS_INVISIBLE_SIZE)
            }
        }))
        const createdResources = localStorageManager.get(localStorageKeys.CREATED_RESOURCES)
        if (lineChartData?.some(({data}) => data?.length > MINIMUM_REQUIRED_DATA_COUNT) && createdResources?.[resource?.id]) {
            delete createdResources[resource?.id]
            localStorageManager.set(localStorageKeys.CREATED_RESOURCES, createdResources)
        }
    }, [resource, lineChartData])

    useEffect(() => {
        if (isAllRegionShowed) {
            setOptions(prevOptions => ({
                ...prevOptions,
                chart: {
                    type: 'line'
                },
                fill: {
                    type: 'solid'
                }
            }))
            setIsLegendShowed(true)
            return
        }

        setOptions(prevOptions => ({
            ...prevOptions,
            chart: {
                type: 'area'
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'vertical',
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.9,
                    stops: [0, 100]
                }
            }
        }))
        setIsLegendShowed(false)
    }, [isAllRegionShowed])

    useEffect(() => {
        if (sidebarIsActive) {
            setTimeout(() => {
                document.querySelectorAll('.apexcharts-reset-icon').forEach((item) => {
                    item.click()
                })
            }, 400)
        }
    }, [sidebarIsActive])

    useEffect(() => {
        setOptions(prevOptions => ({
            ...prevOptions,
            grid: {
                ...prevOptions.grid,
                borderColor: isDarkMode ? colors.colorPrimaryHover : colors.colorLightGrey,
                row: {
                    colors: isDarkMode ? [] : [colors.colorWhite, 'transparent'],
                    show: !isDarkMode
                },
                column: {
                    colors: isDarkMode ? [] : [colors.colorWhite, 'transparent'],
                    show: !isDarkMode
                }
            },
            xaxis: {
                ...prevOptions.xaxis,
                axisBorder: {
                    color: isDarkMode ? colors.colorPrimaryHover : colors.colorLightGrey
                }
            }
        }))
    }, [isDarkMode])

    return (
        <div className={lineChartData?.some(({data}) => data?.length > 59) ? 'two-n' : ''}>
            <Chart options={options} series={series} type={isAllRegionShowed ? 'line' : 'area'} height={300} />
            {isLegendShowed &&
            <CustomLegend
                series={series}
                showRegionsLoader={showRegionsLoader}
                selectRegions={selectRegions}
                toggleSeriesVisibility={toggleSeriesVisibility}
                activeSeries={activeSeries}
            />}
        </div>
    )
}

export default LineChart
